"use client";

import { type ReactNode, useState } from "react";
import { Clock, ExternalLink, FileText, Minimize2, Play } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button, CustomTooltip, TradingViewChart } from "@/app/components/ui";
import { CHAT_MESSAGE_METADATA_TYPE } from "@/app/lib/constants";
import { ChartDataFromMetaData, Top30CoinsTaiFuIndexByIndexResponse } from "@/types";
import { GenericTable, TaiFuTopCoinTable } from "@/app/[locale]/c/[id]/component/tables";
import { FUNCTION_NAME_ENUM, TAI_FU_30_INDEX_CODE } from "@/app/lib/enums";

type ChatMessageMetadataProps = {
   metaData: Record<string, any>[];
};

type SourceSuggestion = {
   title?: string;
   sourceTitle?: string;
   sourceType?: string;
   platform?: string;
   containerName?: string;
   locatorType?: string;
   locationLabel?: string;
   link?: string;
   canonicalUrl?: string;
   summary?: string;
};

const buildMetadataItemKey = (
   metaDataItem: Record<string, any>,
   index: number
): string => {
   if (metaDataItem?.type === CHAT_MESSAGE_METADATA_TYPE.SOURCE_SUGGESTIONS) {
      return `${CHAT_MESSAGE_METADATA_TYPE.SOURCE_SUGGESTIONS}-${index}`;
   }

   if (typeof metaDataItem?.functionName === "string") {
      return `${metaDataItem.functionName}-${index}`;
   }

   if (typeof metaDataItem?.type === "string") {
      return `${metaDataItem.type}-${index}`;
   }

   return `metadata-${index}`;
};

export const ChatMessageMetadata = ({ metaData }: ChatMessageMetadataProps) => {
   if (!Array.isArray(metaData)) return null;
   return metaData.map((metaDataItem: Record<string, any>, index: number) => (
      <MetadataItem
         key={buildMetadataItemKey(metaDataItem, index)}
         metaDataItem={metaDataItem}
      />
   ));
};

const getHostname = (url: string): string => {
   try {
      return new URL(url).hostname.replace(/^www\./, "");
   } catch {
      return "";
   }
};

const getYouTubeVideoId = (url: string): string => {
   try {
      const parsedUrl = new URL(url);
      const host = parsedUrl.hostname.toLowerCase();
      if (host.includes("youtu.be")) {
         return parsedUrl.pathname.split("/").filter(Boolean)[0] || "";
      }
      if (host.includes("youtube.com")) {
         const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
         if (pathParts[0] === "embed" || pathParts[0] === "shorts") {
            return pathParts[1] || "";
         }
         return parsedUrl.searchParams.get("v") || "";
      }
      return "";
   } catch {
      return "";
   }
};

const parseYouTubeTimeToSeconds = (value: string | null): number => {
   if (!value) return 0;

   const normalized = value.trim().toLowerCase();
   if (/^\d+$/.test(normalized)) return Number(normalized);

   const hours = Number(normalized.match(/(\d+)h/)?.[1] || 0);
   const minutes = Number(normalized.match(/(\d+)m/)?.[1] || 0);
   const seconds = Number(normalized.match(/(\d+)s/)?.[1] || 0);
   const totalSeconds = hours * 3600 + minutes * 60 + seconds;

   return Number.isFinite(totalSeconds) ? totalSeconds : 0;
};

const getYouTubeStartSeconds = (url: string): number => {
   try {
      const parsedUrl = new URL(url);
      return (
         parseYouTubeTimeToSeconds(parsedUrl.searchParams.get("start")) ||
         parseYouTubeTimeToSeconds(parsedUrl.searchParams.get("t"))
      );
   } catch {
      return 0;
   }
};

const getYouTubeEmbedUrl = (url: string): string => {
   const videoId = getYouTubeVideoId(url);
   if (!videoId) return "";

   const embedUrl = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`);
   const startSeconds = getYouTubeStartSeconds(url);
   if (startSeconds > 0) {
      embedUrl.searchParams.set("start", String(startSeconds));
   }

   return embedUrl.toString();
};

const getYouTubeThumbnailUrl = (url: string): string => {
   const videoId = getYouTubeVideoId(url);
   return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : "";
};

type SourceInfoTooltipProps = {
   children: ReactNode;
   displayTitle: string;
   locationLabel?: string;
   hostname?: string;
   sourceLabel?: string;
};

const SourceInfoTooltipContent = ({
   displayTitle,
   locationLabel,
   hostname,
   sourceLabel
}: Omit<SourceInfoTooltipProps, "children">) => (
   <div className="space-y-1 text-xs leading-relaxed">
      <div className="font-semibold text-popover-foreground">{displayTitle}</div>
      {(locationLabel || hostname || sourceLabel) && (
         <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground">
            {sourceLabel && <span>{sourceLabel}</span>}
            {hostname && <span>{hostname}</span>}
            {locationLabel && (
               <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3 shrink-0" />
                  {locationLabel}
               </span>
            )}
         </div>
      )}
   </div>
);

const SourceInfoTooltip = ({
   children,
   displayTitle,
   locationLabel,
   hostname,
   sourceLabel
}: SourceInfoTooltipProps) => {
   if (!displayTitle && !locationLabel && !hostname && !sourceLabel) {
      return <>{children}</>;
   }

   return (
      <CustomTooltip
         text={
            <SourceInfoTooltipContent
               displayTitle={displayTitle}
               locationLabel={locationLabel}
               hostname={hostname}
               sourceLabel={sourceLabel}
            />
         }
         delayDuration={250}
         position="top"
         align="start"
         maxWidth="max-w-[min(22rem,calc(100vw-2rem))]"
      >
         {children}
      </CustomTooltip>
   );
};

type YouTubeSourceCardProps = {
   link: string;
   displayTitle: string;
   sourceLabel?: string;
   locationLabel?: string;
   summary?: string;
   openLabel: string;
   playLabel: string;
   collapseLabel: string;
};

const YouTubeSourceCard = ({
   link,
   displayTitle,
   sourceLabel,
   locationLabel,
   summary,
   openLabel,
   playLabel,
   collapseLabel
}: YouTubeSourceCardProps) => {
   const [isPlaying, setIsPlaying] = useState(false);
   const youtubeEmbedUrl = getYouTubeEmbedUrl(link);
   const thumbnailUrl = getYouTubeThumbnailUrl(link);

   if (isPlaying) {
      return (
         <div className="w-full rounded-lg border border-border/70 bg-background/40 p-2 sm:col-span-2">
            <div className="relative overflow-hidden rounded-md bg-black">
               <iframe
                  src={youtubeEmbedUrl}
                  title={displayTitle}
                  className="aspect-video w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
               />
               <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => setIsPlaying(false)}
                  className="absolute right-1.5 top-1.5 h-auto gap-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white hover:bg-black/85 hover:text-white"
                  aria-label={collapseLabel}
               >
                  <Minimize2 className="h-3 w-3" />
                  {collapseLabel}
               </Button>
            </div>
            <div className="mt-1.5 px-0.5">
               <div className="line-clamp-2 text-xs font-semibold leading-snug text-foreground">
                  {displayTitle}
               </div>
               {sourceLabel && (
                  <div className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">
                     {sourceLabel}
                  </div>
               )}
               {locationLabel && (
                  <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                     <Clock className="h-3 w-3" />
                     {locationLabel}
                  </div>
               )}
               {summary && (
                  <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">
                     {summary}
                  </p>
               )}
               <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary"
               >
                  {openLabel}
                  <ExternalLink className="h-3 w-3" />
               </a>
            </div>
         </div>
      );
   }

   return (
      <SourceInfoTooltip
         displayTitle={displayTitle}
         sourceLabel={sourceLabel}
         locationLabel={locationLabel}
      >
         <div className="group/source flex w-full min-w-0 items-center gap-2 rounded-md border border-border/70 bg-background/40 p-1.5 transition-colors hover:border-primary/30 hover:bg-background/60">
            <Button
               type="button"
               variant="ghost"
               size="icon"
               onClick={() => setIsPlaying(true)}
               className="group/play relative h-10 w-14 shrink-0 overflow-hidden rounded-md bg-black p-0 hover:bg-black"
               aria-label={playLabel}
            >
               {thumbnailUrl ? (
                  <img src={thumbnailUrl} alt="" className="h-full w-full object-cover" />
               ) : (
                  <div className="flex h-full w-full items-center justify-center bg-muted">
                     <Play className="h-4 w-4 text-muted-foreground" />
                  </div>
               )}
               <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover/play:bg-black/40">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black/75 text-white">
                     <Play className="h-2.5 w-2.5 fill-current" />
                  </span>
               </span>
            </Button>
            <Button
               type="button"
               variant="ghost"
               onClick={() => setIsPlaying(true)}
               className="h-auto min-w-0 flex-1 justify-start whitespace-normal p-0 text-left hover:bg-transparent"
            >
               <div className="line-clamp-1 text-xs font-medium leading-snug text-foreground">
                  {displayTitle}
               </div>
               {sourceLabel && (
                  <div className="mt-0.5 line-clamp-1 text-[10px] text-muted-foreground">
                     {sourceLabel}
                  </div>
               )}
               {locationLabel && (
                  <div className="mt-0.5 inline-flex max-w-full items-center gap-1 text-[10px] text-muted-foreground">
                     <Clock className="h-2.5 w-2.5 shrink-0" />
                     <span className="truncate">{locationLabel}</span>
                  </div>
               )}
            </Button>
            <Button
               type="button"
               variant="ghost"
               size="icon"
               asChild
               className="h-6 w-6 shrink-0 text-muted-foreground hover:text-primary"
            >
               <a href={link} target="_blank" rel="noreferrer" aria-label={openLabel}>
                  <ExternalLink className="h-3 w-3" />
               </a>
            </Button>
         </div>
      </SourceInfoTooltip>
   );
};

const isSourceSuggestionsMetadata = (metaDataItem: Record<string, any>) => {
   return (
      metaDataItem?.type === CHAT_MESSAGE_METADATA_TYPE.SOURCE_SUGGESTIONS &&
      Array.isArray(metaDataItem?.result?.sources)
   );
};

const SourceSuggestionsCard = ({ sources }: { sources: SourceSuggestion[] }) => {
   const translate = useTranslations("Chat.SourceSuggestions");
   const visibleSources = sources
      .filter((source) => source?.link || source?.canonicalUrl)
      .slice(0, 5);

   if (visibleSources.length === 0) return null;

   return (
      <div className="mt-2">
         <div className="mb-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
            {translate("title")}
         </div>
         <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {visibleSources.map((source, index) => {
               const link = source.link || source.canonicalUrl || "";
               const youtubeEmbedUrl = getYouTubeEmbedUrl(link);
               const hostname = getHostname(source.canonicalUrl || link);
               const displayTitle =
                  source.title || source.sourceTitle || translate("untitled");
               const sourceLabel = source.sourceTitle || source.containerName || "";

               if (youtubeEmbedUrl) {
                  return (
                     <YouTubeSourceCard
                        key={`${link}-${index}`}
                        link={link}
                        displayTitle={displayTitle}
                        sourceLabel={sourceLabel}
                        locationLabel={source.locationLabel}
                        summary={source.summary}
                        openLabel={translate("open")}
                        playLabel={translate("play")}
                        collapseLabel={translate("collapse")}
                     />
                  );
               }

               return (
                  <SourceInfoTooltip
                     key={`${link}-${index}`}
                     displayTitle={displayTitle}
                     locationLabel={source.locationLabel}
                     hostname={hostname}
                     sourceLabel={sourceLabel}
                  >
                     <a
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        className="group/source flex w-full min-w-0 items-center gap-2 rounded-md border border-border/70 bg-background/40 p-1.5 transition-colors hover:border-primary/30 hover:bg-background/60"
                     >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                           <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                           <div className="line-clamp-1 text-xs font-medium leading-snug text-foreground group-hover/source:text-primary">
                              {displayTitle}
                           </div>
                           <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] text-muted-foreground">
                              {sourceLabel && (
                                 <span className="hidden truncate sm:inline">
                                    {sourceLabel}
                                 </span>
                              )}
                              {source.locationLabel && (
                                 <span className="inline-flex min-w-0 items-center gap-1">
                                    <Clock className="h-2.5 w-2.5 shrink-0" />
                                    <span className="truncate">
                                       {source.locationLabel}
                                    </span>
                                 </span>
                              )}
                           </div>
                        </div>
                        <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground group-hover/source:text-primary" />
                     </a>
                  </SourceInfoTooltip>
               );
            })}
         </div>
      </div>
   );
};

const extractChartDataFromMetaData = (
   metaDataItem: Record<string, any>
): ChartDataFromMetaData | null => {
   if (!metaDataItem) return null;
   const indexCodes = Object.values(TAI_FU_30_INDEX_CODE);
   const itemsChart = indexCodes.includes(metaDataItem?.result?.summary?.symbol);
   if (!itemsChart) return null;
   return {
      apiUrl: metaDataItem?.result?.apiUrl,
      symbol: metaDataItem?.result?.summary?.symbol,
      name: metaDataItem?.result?.summary?.name,
      description: metaDataItem?.result?.description
   };
};

const MetadataItem = ({ metaDataItem }: { metaDataItem: Record<string, any> }) => {
   if (isSourceSuggestionsMetadata(metaDataItem)) {
      return <SourceSuggestionsCard sources={metaDataItem.result.sources} />;
   }

   const chartData = extractChartDataFromMetaData(metaDataItem);
   if (chartData) {
      return (
         <div className="my-4 w-full">
            <TradingViewChart
               data={chartData.data}
               apiUrl={chartData.apiUrl}
               symbol={chartData.symbol}
               name={chartData.name}
               description={chartData.description}
               height={400}
               className="w-full"
            />
         </div>
      );
   }

   const isCoinChart =
      metaDataItem.functionName === FUNCTION_NAME_ENUM.getSpecificCoinChartData &&
      metaDataItem.result?.apiUrl;
   if (isCoinChart) {
      return (
         <div className="my-4 w-full">
            <TradingViewChart
               apiUrl={metaDataItem.result.apiUrl}
               symbol={metaDataItem.result?.summary?.symbol || ""}
               name={metaDataItem.result?.summary?.name || ""}
               description={metaDataItem.result?.description || ""}
               height={400}
               className="w-full"
            />
         </div>
      );
   }
   const isListTop30CoinsTaiFuIndex =
      metaDataItem.functionName === FUNCTION_NAME_ENUM.getTop30CoinsTaiFuIndexByIndex;
   if (isListTop30CoinsTaiFuIndex) {
      return (
         <TaiFuTopCoinTable
            data={metaDataItem.result as Top30CoinsTaiFuIndexByIndexResponse}
         />
      );
   }
   return (
      <GenericTable
         data={metaDataItem.result as Record<string, any>[]}
         title={metaDataItem.functionName}
      />
   );
};
